import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';

import {
  TableCell,
  EmptyState,
  Text,
  Checkbox,
  Pagination,
} from 'modules/common';

import PageLoader from '../PageLoader';

const renderHeader = (col) => {
  try {
    return typeof col.label === 'function' ? col?.label(col) : col?.label;
  } catch (error) {
    return '';
  }
};

const getCell = (item, col, i) => {
  try {
    return typeof col.key === 'function' ? col.key(item, i) : item?.[col?.key];
  } catch (err) {
    return '';
  }
};

function GenericTable({
  id,
  data,
  format,
  selected,
  isLoading,
  withHover,
  showHeader,
  pagination,
  tClassName,
  selectedKey,
  onSelectRow,
  trClassName,
  tbodyClassName,
  theadClassName,
  containerClassName,
  emptyStateProps,
  withRounded,
  withBorder,
  withCheckbox,
  thClassName,
  tdClassName,
  withPagination,
  paginationProps,
  noDataHeight,
  maxHeight,
  height,
  checkboxProps,
  withCheckboxHeader,
  isRowDisabled,
  isVerticalAlignTop,
}) {
  const [rows, setRows] = React.useState([]);

  const handleSelectRow = (value) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    // This is brakepoint of tailwind sm screen size
    // const width = window.outerWidth;
    // if (onSelectRow && width > 640) {
    onSelectRow(value);
    // }
  };

  React.useEffect(() => {
    if (!isLoading) {
      setRows(data);
    }
  }, [data, isLoading]);

  React.useLayoutEffect(() => {
    let timer;
    if (selected) {
      const x = document.getElementById(selected);
      timer = setTimeout(() => {
        if (rows?.length && withHover && selected && x) {
          x.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
    return () => clearInterval(timer);
  }, [rows, selected, withHover]);

  const $format = React.useMemo(() => format?.filter((x) => x), [format]);

  const dispatchTableStyle = () => {
    if (rows?.length <= 0 && noDataHeight?.length > 0) {
      return { minHeight: noDataHeight, maxHeight, height };
    }

    if (noDataHeight?.length > 0) {
      return { minHeight: 'auto', maxHeight, height };
    }

    return {
      maxHeight,
    };
  };

  const handleOnRowClick = (item, e) => {
    if (typeof isRowDisabled === 'function' && isRowDisabled(item)) {
      return false;
    }

    return onSelectRow ? handleSelectRow(item)(e) : () => {};
  };

  return (
    <div className="flex flex-col gap-ds-12 flex-1 relative w-full h-full md:min-h-0 overflow-hidden">
      <div
        className={cn(
          'relative w-full h-full md:min-h-0 overflow-hidden bg-white',
          {
            rounded: withRounded,
          }
        )}
      >
        {rows?.length === 0 && !isLoading && (
          <div className={cn('flex justify-center')}>
            <div
              className={cn('absolute z-10 top-1/2 -translate-y-1/2', {
                'mt-ds-20': $format?.length > 0 && showHeader,
              })}
            >
              <EmptyState {...emptyStateProps} />
            </div>
          </div>
        )}

        {isLoading && (
          <div
            className={cn(
              'absolute inset-0 z-10 bg-white flex flex-col overflow-hidden',
              {
                rounded: withRounded,
                'border border-default': withBorder,
              }
            )}
          >
            <div className="md:m-auto">
              <PageLoader />
            </div>
          </div>
        )}

        <div
          id="_ds-table-container"
          className={cn('_ds-table-container', {
            [containerClassName]: containerClassName,
            rounded: withRounded,
            'border border-default': withBorder,
          })}
          style={dispatchTableStyle()}
        >
          <div id={id} className="_ds-table-inner-container scrollbar">
            <table
              className={cn('_table', {
                [tClassName]: tClassName,
              })}
            >
              {showHeader && (
                <thead
                  className={cn('_ds-table-head border-b border-default', {
                    [theadClassName]: theadClassName,
                    'rounded-t': withRounded,
                  })}
                >
                  <tr>
                    {withCheckbox && (
                      <th className={cn('w-4', thClassName)}>
                        {withCheckboxHeader && <Checkbox name="checkbox-all" />}
                      </th>
                    )}

                    {$format.map((col, i) => {
                      const {
                        stackKey,
                        widthClassName = false,
                        stackKeyCLassName = 'hidden md:table-cell',
                      } = col;

                      return (
                        <th
                          // eslint-disable-next-line react/no-array-index-key
                          key={i}
                          className={cn('text-left', thClassName, {
                            [stackKeyCLassName]: stackKey,
                            [widthClassName]: widthClassName,
                          })}
                        >
                          <Text type="overline" color="muted">
                            {renderHeader(col)}
                          </Text>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
              )}

              <tbody
                className={cn('_ds-table-body', {
                  [tbodyClassName]: tbodyClassName,
                })}
              >
                {rows.length < 1 ? (
                  <tr />
                ) : (
                  rows.map((item, i) => {
                    const isSelected = `${selected}` === `${item[selectedKey]}`;

                    const current_page = pagination?.current_page ?? 0;
                    const per_page = pagination?.per_page ?? 0;

                    return (
                      <tr
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        className={cn('_ds-table-row', {
                          '_ds-table-row-selected': isSelected,
                          [trClassName]: trClassName,
                          '_ds-table-row-hover': withHover && !isSelected,
                          'cursor-default': !onSelectRow,
                          'cursor-pointer': onSelectRow,
                          '!cursor-not-allowed':
                            typeof isRowDisabled === 'function' &&
                            isRowDisabled(item),
                        })}
                        onClick={(e) => handleOnRowClick(item, e)}
                      >
                        {withCheckbox && (
                          <td
                            className={cn('w-4', tdClassName, {
                              'align-top': isVerticalAlignTop,
                            })}
                          >
                            <Checkbox
                              {...checkboxProps}
                              onChange={
                                typeof checkboxProps?.onChange === 'function'
                                  ? (v) => checkboxProps?.onChange(v, item, i)
                                  : checkboxProps?.onChange
                              }
                              value={
                                typeof checkboxProps?.value === 'function'
                                  ? checkboxProps?.value(item, i)
                                  : checkboxProps?.value
                              }
                            />
                          </td>
                        )}

                        {$format.map((col, subI) => {
                          const {
                            stackKey,
                            widthClassName = false,
                            stackKeyCLassName = 'hidden md:table-cell',
                          } = col;

                          if (col.key === '_count') {
                            return (
                              <td
                                // eslint-disable-next-line react/no-array-index-key
                                key={subI}
                                className={cn(tdClassName, {
                                  [widthClassName]: widthClassName,
                                  'align-top': isVerticalAlignTop,
                                })}
                              >
                                <span className="font-semibold">
                                  {(current_page - 1) * per_page + i + 1}.
                                </span>
                              </td>
                            );
                          }

                          return (
                            <td
                              // eslint-disable-next-line react/no-array-index-key
                              key={subI}
                              className={cn(tdClassName, {
                                [stackKeyCLassName]: stackKey,
                                [widthClassName]: widthClassName,
                                'align-top': isVerticalAlignTop,
                              })}
                            >
                              <TableCell
                                label={getCell(item, col, i)}
                                className={col?.className || ''}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {withPagination && (
        <Pagination small isLoading={isLoading} {...paginationProps} />
      )}
    </div>
  );
}

GenericTable.defaultProps = {
  id: 'generic-table',
  data: [],
  format: [],
  selected: '',
  withHover: true,
  isLoading: false,
  showHeader: true,
  tClassName: false,
  selectedKey: 'id',
  pagination: false,
  trClassName: false,
  onSelectRow: false,
  withCheckbox: false,
  theadClassName: false,
  tbodyClassName: false,
  containerClassName: false,
  withRounded: true,
  withBorder: true,
  withCheckboxHeader: true,
  emptyStateProps: {},
  thClassName: '',
  tdClassName: '',
  withPagination: false,
  paginationProps: {},
  noDataHeight: '',
  maxHeight: 'auto',
  height: 'auto',
  checkboxProps: {},
  isRowDisabled: false,
  isVerticalAlignTop: false,
};

GenericTable.propTypes = {
  emptyStateProps: PropTypes.instanceOf(Object),
  paginationProps: PropTypes.instanceOf(Object),
  checkboxProps: PropTypes.instanceOf(Object),
  isRowDisabled: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Function),
  ]),
  id: PropTypes.string,
  withHover: PropTypes.bool,
  isVerticalAlignTop: PropTypes.bool,
  withCheckboxHeader: PropTypes.bool,
  withRounded: PropTypes.bool,
  withPagination: PropTypes.bool,
  maxHeight: PropTypes.string,
  height: PropTypes.string,
  withBorder: PropTypes.bool,
  withCheckbox: PropTypes.bool,
  isLoading: PropTypes.bool,
  showHeader: PropTypes.bool,
  selectedKey: PropTypes.string,
  thClassName: PropTypes.string,
  noDataHeight: PropTypes.string,
  tdClassName: PropTypes.string,
  pagination: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Object),
  ]),
  data: PropTypes.instanceOf(Array),
  format: PropTypes.instanceOf(Array),
  onSelectRow: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  trClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  tbodyClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  theadClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  containerClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

export default React.memo(GenericTable);
